import { useCallback, useEffect } from "react"
import screenfull from "screenfull"
import { Logger } from "../../debug/logger.shared"
import {
  EMPTY,
  catchError,
  defer,
  from,
  mergeMap,
  retry,
  throwError,
  timer,
} from "rxjs"
import { useSubscribe } from "reactjrx"
import { fromCreateDialog } from "../dialogs/fromCreateDialog"
import { CancelError } from "../../errors/errors.shared"

const isPermissionCheckFailedError = (error: unknown): error is TypeError =>
  error instanceof TypeError &&
  // chrome
  (error.message === "Permissions check failed" ||
    // safari
    error.message === "Type error" ||
    // firefox
    error.message === "Fullscreen request denied")

export const useFullscreenOnMount = ({ enabled }: { enabled: boolean }) => {
  const fullscreenOnMount = useCallback(() => {
    if (enabled && screenfull.isEnabled && !screenfull.isFullscreen) {
      return defer(() => {
        return from(screenfull.request(undefined, { navigationUI: "hide" }))
      }).pipe(
        retry({
          count: 1,
          delay: (error) => {
            if (isPermissionCheckFailedError(error)) {
              return timer(5).pipe(
                mergeMap(() =>
                  // we avoid double dialog because of strict mode
                  screenfull.isFullscreen
                    ? throwError(() => error)
                    : fromCreateDialog({
                        title: "Fullscreen request",
                        message:
                          "Your browser does not allow automatic fullscreen without an interaction",
                        actions: [{ title: "Fullscreen" }],
                        cancellable: true,
                      }),
                ),
              )
            }

            throw error
          },
        }),
        catchError((error) => {
          if (
            isPermissionCheckFailedError(error) ||
            error instanceof CancelError
          ) {
            return EMPTY
          }

          Logger.error(error)

          return EMPTY
        }),
      )
    }

    return EMPTY
  }, [enabled])

  useSubscribe(fullscreenOnMount)

  useEffect(() => {
    return () => {
      if (screenfull.isEnabled && screenfull.isFullscreen) {
        screenfull.exit().catch(Logger.error)
      }
    }
  }, [])
}
